import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../app/hooks";
import { redeemSignInTokenAsync } from "../features/authentication/authenticationApi";
import { resolvePathAfterSignIn } from "../features/contexts/utils/postSignIn";
import { ROUTES } from "../routes";

/** Instructor magic-link landing: redeems the token from the URL and signs in. */
const LoginVerifyPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [failed, setFailed] = useState(false);
  const redeemedRef = useRef(false);

  const token = searchParams.get("token");

  useEffect(() => {
    // A magic link is single-use — guard against StrictMode double-invocation.
    if (token && !redeemedRef.current) {
      redeemedRef.current = true;
      dispatch(redeemSignInTokenAsync(token))
        .unwrap()
        .then(() => resolvePathAfterSignIn(dispatch))
        .then((path) => navigate(path, { replace: true }))
        .catch(() => setFailed(true));
    }
  }, [token, dispatch, navigate]);

  return (
    <section className="mx-auto w-full max-w-md px-4 py-12 text-center">
      {!token || failed ? (
        <>
          <h1 className="mb-2 text-2xl font-bold text-grey-20 dark:text-white">
            {t("auth.passwordless.linkInvalidTitle", "This link doesn't work anymore")}
          </h1>
          <p className="mb-6 text-sm text-grey-40 dark:text-grey-80">
            {t(
              "auth.passwordless.linkInvalidBody",
              "Sign-in links work once and expire after 15 minutes. Request a new one and try again.",
            )}
          </p>
          <Link to={ROUTES.LOGIN} className="btn btn-primary inline-block">
            {t("auth.passwordless.backToSignIn", "Back to sign in")}
          </Link>
        </>
      ) : (
        <p className="text-sm text-grey-40 dark:text-grey-80">
          {t("auth.passwordless.signingIn", "Signing you in…")}
        </p>
      )}
    </section>
  );
};

export default LoginVerifyPage;
